import * as React from 'react'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listClients, addClientFacility, inviteUserToClient } from '@/features/admin/api'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { formatPhoneInput, combinePhoneCode } from '@/lib/phone'
import '../ManagePages.css'

interface Props {
  open: boolean
  onClose: () => void
  /** Pre-select a tenant when opening from the org page */
  defaultTenantId?: string
}

export function AddFacilityDrawer({ open, onClose, defaultTenantId }: Props): React.JSX.Element {
  const toast = useToast()
  const qc = useQueryClient()

  const [tenantId, setTenantId] = useState(defaultTenantId ?? '')
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCode, setPhoneCode] = useState('+1')

  // Admin account (optional) — creates a SUPER_ADMIN for the org
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [showAdminPw, setShowAdminPw] = useState(false)

  const clientsQuery = useQuery({ queryKey: ['clients'], queryFn: listClients, enabled: open })
  const clients = clientsQuery.data ?? []

  function reset(): void {
    setTenantId(defaultTenantId ?? '')
    setName(''); setAddress(''); setCity(''); setState(''); setZip(''); setPhone(''); setPhoneCode('+1')
    setAdminName(''); setAdminEmail(''); setAdminPassword(''); setShowAdminPw(false)
  }

  const add = useMutation({
    mutationFn: async () => {
      const fac = await addClientFacility(tenantId, {
        name,
        address_line1: address || undefined,
        city: city || undefined,
        state: state || undefined,
        postal_code: zip || undefined,
        phone: combinePhoneCode(phone, phoneCode) || undefined,
      })
      if (adminName.trim() && adminEmail.trim()) {
        await inviteUserToClient(tenantId, {
          name: adminName.trim(),
          email: adminEmail.trim(),
          role: 'SUPER_ADMIN',
          password: adminPassword || undefined,
        })
      }
      return fac
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-facilities'] })
      void qc.invalidateQueries({ queryKey: ['client-facilities', tenantId] })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
      reset()
      onClose()
      toast.success('Facility added', adminEmail.trim() ? 'Facility created and admin invited.' : 'The new facility is now active.')
    },
    onError: (err) =>
      toast.error('Could not add facility', err instanceof ApiError ? err.message : 'Try again.'),
  })

  const canSubmit = tenantId && name.trim()

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add facility"
      subtitle="Add a new physical site to an existing organization."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={add.isPending}>Cancel</Button>
          <Button onClick={() => add.mutate()} loading={add.isPending} disabled={!canSubmit}>
            Add facility
          </Button>
        </>
      }
    >
      <div className="drawer-sections">
        <div className="drawer-section">
          <div className="drawer-section-label">Organization</div>
          <FormField label="Select organization" htmlFor="af-org" required>
            <select
              id="af-org"
              className="select"
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
            >
              <option value="">— choose an organization —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
        </div>

        <div className="drawer-section">
          <div className="drawer-section-label">Facility details</div>
          <FormField label="Facility name" htmlFor="af-name" required>
            <Input id="af-name" value={name} onChange={e => setName(e.target.value)} placeholder="Sunrise North" />
          </FormField>
          <div className="drawer-phone-row">
            <FormField label="Code" htmlFor="af-phone-code">
              <select id="af-phone-code" className="select" value={phoneCode} onChange={e => setPhoneCode(e.target.value)}>
                <option value="+1">+1</option>
                <option value="+91">+91</option>
                <option value="+41">+41</option>
              </select>
            </FormField>
            <FormField label="Phone" htmlFor="af-phone">
              <Input
                id="af-phone"
                type="tel"
                value={phone}
                onChange={e => setPhone(formatPhoneInput(e.target.value))}
                placeholder="(312) 555-0100"
              />
            </FormField>
          </div>
          <FormField label="Address" htmlFor="af-addr">
            <Input id="af-addr" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" />
          </FormField>
          <div className="drawer-3col">
            <FormField label="City" htmlFor="af-city">
              <Input id="af-city" value={city} onChange={e => setCity(e.target.value)} placeholder="Chicago" />
            </FormField>
            <FormField label="State" htmlFor="af-state">
              <Input id="af-state" value={state} onChange={e => setState(e.target.value)} placeholder="IL" maxLength={2} />
            </FormField>
            <FormField label="ZIP" htmlFor="af-zip">
              <Input id="af-zip" value={zip} onChange={e => setZip(e.target.value)} placeholder="60601" />
            </FormField>
          </div>
        </div>

        <div className="drawer-section">
          <div className="drawer-section-label">Admin account (optional)</div>
          <div className="drawer-2col">
            <FormField label="Full name" htmlFor="af-aname">
              <Input id="af-aname" value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="Jane Smith" />
            </FormField>
            <FormField label="Email" htmlFor="af-aemail">
              <Input id="af-aemail" type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="jane@facility.com" />
            </FormField>
          </div>
          <FormField label="Password (optional)" htmlFor="af-apw">
            <div className="pw-wrap">
              <Input
                id="af-apw"
                type={showAdminPw ? 'text' : 'password'}
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
                placeholder="Leave blank to send invite email"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="pw-eye"
                onClick={() => setShowAdminPw(v => !v)}
                tabIndex={-1}
                aria-label={showAdminPw ? 'Hide password' : 'Show password'}
              >
                {showAdminPw ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </FormField>
        </div>
      </div>
    </Drawer>
  )
}

function EyeIcon(): React.JSX.Element {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
}
function EyeOffIcon(): React.JSX.Element {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
}
